/**
 * SearchResultItem Component
 * 
 * Displays a single search result (conversation or message match).
 * Shows:
 * - Conversation/user avatar
 * - Name with match highlighting
 * - Message snippet with match highlighting (for message results)
 * - Timestamp
 */

import React from 'react';
import { MessageSquare, Users, Search } from 'lucide-react';
import { formatConversationTime } from './utils/timeFormat';
import { CONVERSATION_TYPES } from '@/lib/firestore';
import { UserAvatar } from '@/components/shared';
import { useAvatarCache } from '@/contexts';

/**
 * Highlight matching text in a string
 */
function HighlightedText({ text, highlight }) {
  if (!highlight || !text) return <span>{text}</span>;
  
  const lowerText = text.toLowerCase();
  const lowerHighlight = highlight.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerHighlight);
  
  if (matchIndex === -1) return <span>{text}</span>;
  
  const before = text.slice(0, matchIndex);
  const match = text.slice(matchIndex, matchIndex + highlight.length);
  const after = text.slice(matchIndex + highlight.length);
  
  return (
    <span>
      {before}
      <mark data-ui="hit" className="bg-yellow-200 text-yellow-900 rounded px-0.5">{match}</mark>
      {after}
    </span>
  );
}

/**
 * Avatar component for search results
 */
function ResultAvatar({ name, userId, avatarUrl, isGroup, isMessage }) {
  if (isMessage) {
    return (
      <div data-ui="avatar-msg" className="w-10 h-10 rounded-full bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center">
        <MessageSquare className="w-5 h-5 text-slate-400 dark:text-slate-500" />
      </div>
    );
  }

  if (isGroup) {
    return (
      <div data-ui="avatar-group" className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-sm">
        <Users className="w-4 h-4" />
      </div>
    );
  }

  return (
    <UserAvatar userId={userId} name={name} avatarUrl={avatarUrl} size="md" />
  );
}

/**
 * Search result item for a conversation match
 */
export function ConversationResultItem({ result, searchQuery, onClick, isSelected, currentUserId }) {
  const { conversation, displayName } = result;
  const isGroup = conversation.type === CONVERSATION_TYPES.GROUP;
  const { getCachedAvatar } = useAvatarCache();
  
  // For DMs, get the other participant's ID and avatar (cache-first, Firestore fallback)
  const otherParticipantId = !isGroup 
    ? conversation.participants?.find(id => id !== currentUserId)
    : null;
  const otherParticipantAvatar = otherParticipantId 
    ? (getCachedAvatar(otherParticipantId) || conversation.participantDetails?.[otherParticipantId]?.avatar)
    : null;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left
        ${isSelected 
          ? 'bg-blue-50 dark:bg-blue-950/40 border-2 border-blue-200 dark:border-blue-900' 
          : 'hover:bg-slate-50 dark:hover:bg-white/5 border-2 border-transparent'
        }
      `}
      data-ui="convo-row"
      data-selected={isSelected ? 'true' : undefined}
      data-testid={`search-result-conversation-${conversation.id}`}
    >
      <ResultAvatar 
        name={displayName} 
        userId={otherParticipantId}
        avatarUrl={otherParticipantAvatar}
        isGroup={isGroup} 
      />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-slate-800 dark:text-slate-100 truncate">
            <HighlightedText text={displayName} highlight={searchQuery} />
          </span>
          {conversation.lastMessage?.timestamp && (
            <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
              {formatConversationTime(conversation.lastMessage.timestamp)}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
          {conversation.lastMessage?.text || 'No messages yet'}
        </p>
      </div>
    </button>
  );
}

/**
 * Search result item for a message match
 */
export function MessageResultItem({ result, searchQuery, onClick, isSelected }) {
  const { conversation, message, displayName, snippet } = result;
  const isGroup = conversation.type === CONVERSATION_TYPES.GROUP;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left
        ${isSelected 
          ? 'bg-blue-50 dark:bg-blue-950/40 border-2 border-blue-200 dark:border-blue-900' 
          : 'hover:bg-slate-50 dark:hover:bg-white/5 border-2 border-transparent'
        }
      `}
      data-ui="convo-row"
      data-selected={isSelected ? 'true' : undefined}
      data-testid={`search-result-message-${message.id}`}
    >
      <ResultAvatar name={displayName} isGroup={isGroup} isMessage />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-slate-700 dark:text-slate-200 truncate text-sm">
            {displayName}
          </span>
          <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
            {formatConversationTime(message.createdAt)}
          </span>
        </div>
        
        {/* Sender name for group messages */}
        {isGroup && message.senderName && (
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {message.senderName}
          </p>
        )}
        
        {/* Message snippet with highlighted match */}
        <p className="text-sm text-slate-600 dark:text-slate-300 truncate mt-0.5">
          <HighlightedText text={snippet || message.text} highlight={searchQuery} />
        </p>
      </div>
    </button>
  );
}

/**
 * Empty search results state
 */
export function EmptySearchResults({ searchQuery }) {
  return (
    <div data-ui="empty" className="flex flex-col items-center justify-center py-12 px-8 text-center" data-testid="empty-search-results">
      <div data-ui="empty-glyph" className="w-16 h-16 rounded-full bg-slate-100 dark:bg-[#3a3d41] flex items-center justify-center mb-4">
        <Search className="w-8 h-8 text-slate-400 dark:text-slate-500" />
      </div>
      <h3 data-ui="empty-title" className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1">No results found</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
        No conversations or messages match "{searchQuery}"
      </p>
      <p className="text-xs text-slate-400 dark:text-slate-500">Try a different search term</p>
    </div>
  );
}

/**
 * Search results loading skeleton
 */
export function SearchResultsSkeleton() {
  return (
    <div className="space-y-2 p-3" data-testid="search-results-loading">
      {[1, 2, 3].map(i => (
        <div 
          key={i} 
          className="flex items-center gap-3 p-3 rounded-xl"
          style={{ animationDelay: `${i * 100}ms` }}
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer rounded-md w-2/3" />
            <div className="h-3 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] animate-shimmer rounded-md w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
